import React, { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoundIcon, RoundIconVariants } from '@components/atoms/RoundIcon';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Card } from '@components/atoms/Card';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import styles from './SpaceCard.module.css';
import { SITE_URLS } from '@constants';
import { Modules, useModule } from '@hooks/useModule';
import { displayPrice } from '@utils/products';
import { Button, ButtonMode } from '@components/atoms/Button';
import { EnergyClassification } from '@components/atoms/EnergyClassification';
import { useToggleFavoriteSpace } from '@services/useToggleFavoriteSpace';

export interface SpaceCardProps {
    address: string;
    uuid?: string;
    merchant?: string;
    name?: string;
    image?: string;
    price?: number;
    oldPrice?: number;
    initialFavorite: boolean;
    promo?: number | string; // #TODO: format will be updated on BE
    truncateTitle?: boolean;

    latitude: string;
    longitude: string;

    energyClassification?: string;
    areaM2?: string;
}

export const SpaceCard: FC<SpaceCardProps> = memo(
    ({
        uuid,
        merchant,
        name,
        image,
        price,
        oldPrice,
        initialFavorite,
        promo,
        truncateTitle,
        energyClassification,
        areaM2,
        address,
    }) => {
        const { isModuleActive } = useModule();
        const {
            request: requestToggleFavorite,
            loading: loadingToggleFavorite,
            error: errorToggleFavorite,
            lastResponseTime: lastToggleFavorite,
        } = useToggleFavoriteSpace();
        
        const { t } = useTranslation();

        const translations = {
            priceOnRequest: t('space-card-component.price-on-request'),
            perMonth: t('space-card-component.per-month'),
            semiNew: t('space-card-component.semi-new'),
            viewDetails: t('space-card-component.view-details'),
        };

        const [favorite, setFavorite] = useState<boolean>(initialFavorite || false);

        const handleOnClickFavorite = () => {
            if (!loadingToggleFavorite && uuid) {
                requestToggleFavorite({ uuid });
            }
        };

        useEffect(() => {
            if (lastToggleFavorite && !errorToggleFavorite) {
                setFavorite(!favorite);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lastToggleFavorite]);

        return (
            <Card classes={styles['space-card']}>
                <div className={`${styles.overlay} d-flex justify-content-between`}>
                    <div />

                    {/* set/unset as favorite */}
                    {isModuleActive(Modules.favorite) && (
                        <RoundIcon
                            icon={`${favorite ? 'fa-solid' : 'fa-light'} fa-heart`}
                            variant={RoundIconVariants.variant3}
                            onClick={handleOnClickFavorite}
                        />
                    )}
                </div>
                <Anchor
                    href={SITE_URLS.spaceDetail.replace(':uuid', `${uuid}`)}
                    classes="text-decoration-none"
                    mode={AnchorMode.none}>
                    <>
                        <div className={`position-relative ${styles['card-sizing']}`}>
                            <Card.ImageOverlay />
                            <img src={image} className="card-img" alt={name} />
                        </div>
                        <div className="card-body px-0">
                            <Card.Title
                                typography={{
                                    tagVariant: TypographyTagVariants.h5,
                                    classes: `${styles.title} ${truncateTitle && styles['truncate-title']}`,
                                    color: 'primary',
                                    fontSize: '16',
                                    children: name,
                                }}
                                underlineAnimation={true}
                            />
                            <div>
                                <Typography classes="d-inline-block" color="primary" fontWeight="900" fontSize="24">
                                    {displayPrice(price) ? `${price}€` : translations.priceOnRequest}
                                </Typography>
                                {displayPrice(price) && (
                                    <Typography classes="d-inline-block ms-1" color="primary" fontWeight="900" fontSize="18">
                                        {` ${translations.perMonth}`}
                                    </Typography>
                                )}
                            </div>

                            <Typography classes="text-truncate my-1" color="dark" fontSize="16" fontWeight="900">
                                {merchant}
                            </Typography>

                            <Typography classes="text-truncate my-1" color="dark" fontSize="16">
                                {address}
                            </Typography>

                            <Typography classes="text-truncate my-1" color="muted" fontSize="14">
                                {merchant} ∙ {areaM2} m2 ∙ {translations.semiNew}{' '}
                                {energyClassification && (
                                    <>
                                        ∙ <EnergyClassification letter={energyClassification} />
                                    </>
                                )}
                            </Typography>

                            <Button mode={ButtonMode.link} label={translations.viewDetails} />
                        </div>
                    </>
                </Anchor>
            </Card>
        );
    }
);
